<template>
  <div class="page content" @keyup.enter="save()">
    <div class="block">
      <h1 class="title">{{ $t('settings.title') }}</h1>
    </div>

    <div class="block">
      <div class="columns">
        <div class="column is-one-third">
          <h3>{{ $t('settings.change_language.title') }}</h3>
        </div>
        <div class="column is-two-thirds">
          <div class="select">
            <select
              name="change-langueage"
              id="change-language"
              v-model="selectedLocale"
            >
              <option
                v-for="(item, index) in locales"
                :key="index"
                :value="item.value"
                >{{ item.label }}</option
              >
            </select>
          </div>
        </div>
      </div>
    </div>

    <div class="block">
      <div class="columns">
        <div class="column is-one-third">
          <h3>{{ $t('settings.change_password.title') }}</h3>
          <p class="help">
            {{ $t(`login_form.${userType}_password_strength`) }}
          </p>
        </div>

        <div class="column is-two-thirds">
          <div class="field is-horizontal">
            <div class="field-body">
              <div class="field">
                <label for="old-password" class="label">{{
                  $t('settings.change_password.old_password_label')
                }}</label>

                <div class="control">
                  <input
                    type="password"
                    id="old-password"
                    class="input is-shadowless"
                    required
                    v-model="oldPassword"
                  />
                </div>
              </div>

              <div class="field">
                <label for="new-password" class="label">{{
                  $t('settings.change_password.new_password_label')
                }}</label>

                <div class="control">
                  <input
                    type="password"
                    id="new-password"
                    class="input is-shadowless"
                    required
                    v-model="newPassword"
                  />
                </div>
              </div>
            </div>
          </div>
          <p class="help is-danger" v-if="passwordHint.length > 0">
            {{ passwordHint }}
          </p>
        </div>
      </div>
    </div>

    <div class="block" v-if="userType === 'journeys'">
      <div class="columns">
        <div class="column is-one-third">
          <h3>{{ $t('settings.delete_user.title') }}</h3>
          <p class="help">{{ $t('settings.delete_user.description') }}</p>
        </div>
        <div class="column is-two-thirds">
          <a class="button is-danger" @click="isModalActive = true">{{
            $t('settings.delete_user.button')
          }}</a>
        </div>
      </div>
    </div>

    <div class="block block-footer">
      <div class="field is-grouped">
        <p class="control">
          <a class="button is-text" @click="cancel()">{{
            $t('settings.actions.cancel')
          }}</a>
        </p>
        <p class="control">
          <button
            class="button is-info"
            :class="{ 'is-loading': isLoading }"
            @click="save()"
          >
            {{ $t('settings.actions.save') }}
          </button>
        </p>
      </div>
    </div>

    <div
      class="modal"
      :class="{ 'is-active': isModalActive }"
      @keyup.esc="closeModal()"
    >
      <div class="modal-background"></div>
      <router-view v-if="isDeleted"/>
      <div v-else class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">
            {{ $t('settings.delete_user.modal.title') }}
          </p>
          <button class="delete" aria-label="close" @click="closeModal()">
            <i class="mdi mdi-close"></i>
          </button>
        </header>
        <section class="modal-card-body">
          <p>{{ $t('settings.delete_user.modal.paragraph_1') }}</p>
          <p>{{ $t('settings.delete_user.modal.paragraph_2') }}</p>
          <div class="field">
            <label for="confirm-password" class="label">{{
              $t('settings.delete_user.modal.confirm_password_label')
            }}</label>
            <div class="control">
              <input
                type="password"
                id="confirm-password"
                class="input is-shadowless"
                autocomplete="current-password"
                required
                v-model="confirmPassword"
              />
            </div>
          </div>
          <p class="help is-danger" v-if="deleteHint.length > 0">
            {{ deleteHint }}
          </p>
        </section>
        <footer class="modal-card-foot">
          <button class="button is-text" @click="closeModal()">
            {{ $t('settings.delete_user.modal.keep_user_button') }}
          </button>
          <button
            class="button is-danger"
            :class="{ 'is-loading': isDeleteLoading }"
            :disabled="!confirmPassword || confirmPassword.length === 0"
            @click="deleteUser()"
          >
            {{ $t('settings.delete_user.button') }}
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import { get } from 'lodash';
import { mapState, mapGetters } from 'vuex';
import Api from 'services/Api';
import Auth from 'services/Auth';
import Cognito from 'services/Cognito';
import notifications from 'services/Notifications';
import { isPasswordValid } from 'utils/auth';

export default {
  name: 'Settings',
  data() {
    return {
      selectedLocale: null,

      oldPassword: '',
      newPassword: '',
      passwordHint: '',

      confirmPassword: '',
      deleteHint: '',

      isLoading: false,
      isDeleteLoading: false,
      isDeleted: false,
      isModalActive: false,
    };
  },
  computed: {
    ...mapState('account', ['email', 'userId', 'userType']),
    ...mapGetters('account', ['locale', 'locales']),
  },
  created() {
    this.selectedLocale = this.locale;
  },
  methods: {
    setLocale(value) {
      this.$store.dispatch('account/setLocale', value);
    },
    async setPassword(newPassword, oldPassword) {
      try {
        if (oldPassword.length === 0) {
          throw new Error(
            this.$t('settings.change_password.errors.password_empty')
          );
        }

        if (newPassword === oldPassword) {
          throw new Error(
            this.$t('settings.change_password.errors.passwords_identical')
          );
        }

        const strength = this.userType === 'journeys' ? 3 : 4;

        if (!isPasswordValid(newPassword, strength)) {
          throw new Error(
            this.$t(`login_form.${this.userType}_password_strength`)
          );
        }

        if (this.userType === 'journeys') {
          await Api.put('auth/reset-password', {
            old_password: oldPassword,
            new_password: newPassword,
          });
        } else {
          const user = await Cognito.currentAuthenticatedUser();

          await Cognito.changePassword(user, oldPassword, newPassword);
        }
      } catch (error) {
        // For error thrown in UI validation
        if (error.message) {
          throw new Error(error.message);
        }
        // For errors sent from server
        // Show the error sent by the server, could be old password entered was wrong or password strength error
        // To Do: Send appropriate error code from server and use that code to show errors with internationalization.
        if (error.status === 400 && error.body && error.body.message) {
          throw new Error(error.body.message);
        }
        // Show generic error if the error is unexpected
        throw new Error(
          this.$t('settings.change_password.errors.change_password_failed')
        );
      }
    },
    async save() {
      this.isLoading = true;
      this.passwordHint = '';

      let counter = 0;

      if (this.selectedLocale !== this.locale) {
        await this.setLocale(this.selectedLocale);
        counter++;
      }

      if (this.newPassword.length > 0) {
        try {
          await this.setPassword(this.newPassword, this.oldPassword);
          counter++;
          this.passwordHint = '';
          this.oldPassword = '';
          this.newPassword = '';
        } catch (err) {
          this.passwordHint = err.message;
        }
      }

      this.isLoading = false;

      if (counter > 0) {
        notifications.add(this.$t('settings.save.success'), 'success');
      }
    },
    async deleteUser() {
      this.deleteHint = '';
      this.isDeleteLoading = true;

      try {
        await Auth.login(this.email, this.confirmPassword);
        await Api.delete(`users/${this.userId}`);
        this.isDeleted = true
        this.$router.push({ name: 'deletion-request-confirmation' })
      } catch (err) {
        this.deleteHint = err.message;
      }

      this.isDeleteLoading = false;
    },
    cancel() {
      this.selectedLocale = this.locale;
      this.passwordHint = '';
      this.oldPassword = '';
      this.newPassword = '';
    },
    closeModal() {
      this.isModalActive = false;
      this.confirmPassword = '';
      this.deleteHint = '';
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~scss/utilities/initial-variables.scss';
@import '~scss/utilities/derived-variables.scss';

$container-padding: 1.625rem;

.content {
  font-family: $family-primary;
  background-color: $white;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba($black, 0.06);

  .label {
    font-weight: 500;
  }

  h3 {
    font-weight: 500;
    font-size: 1.25rem;
    color: rgba($black, 0.72);
    letter-spacing: 0.2px;
  }
}

.block {
  padding: $container-padding;

  &-footer {
    display: flex;
    justify-content: flex-end;
  }
}

.block {
  &:not(:last-child) {
    margin-bottom: 0;
    border-bottom: 1px solid rgba($black, 0.04);
  }
}

.title {
  position: relative;
  font-size: 0.875rem;
  font-weight: 400;
  color: rgba($black, 0.87);
  letter-spacing: 0.2px;

  &:before {
    content: '';
    position: absolute;
    width: 0.375rem;
    height: 1.75rem;
    left: -$container-padding;
    top: -50%;
    border-top-right-radius: 2px;
    border-bottom-right-radius: 2px;
    background-image: linear-gradient(348deg, #83bcff, #4a90e2);
  }
}

.help {
  font-family: $family-secondary;
  color: rgba($black, 0.4);
  font-size: 0.6875rem;
  letter-spacing: 0.2px;

  &.is-danger {
    color: $danger;
  }
}

.button {
  font-family: $family-primary;
  padding: 0.5rem 1.25rem;
  border-radius: 0.25rem;
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.2px;
  color: $white;
  transition: $transition;

  &.is-text {
    color: rgba($black, 0.87);
    font-weight: normal;
    text-decoration: none;

    &:hover,
    &.is-hovered,
    &:focus,
    &.is-focused,
    &:active,
    &.is-active {
      background-color: transparent;
      color: $blue;
    }
  }
}

.modal {
  font-family: $family-primary;

  p {
    font-family: $family-secondary;
  }

  .delete {
    align-self: flex-start;
    background-color: transparent;

    &:before,
    &:after {
      display: none;
    }

    &:hover,
    &.is-hovered {
      i {
        color: rgba($black, 0.24);
      }
    }

    i {
      position: absolute;
      top: 0;
      left: 0;
      font-size: 20px;
      line-height: 20px;
      color: rgba($black, 0.16);
      transition: $transition;
    }
  }

  .input {
    max-width: 400px;
  }
}

.modal-card-title {
  margin-bottom: 0 !important;
}

.modal-card-foot {
  display: flex;
  justify-content: flex-end;
}
</style>
